import { useState, useRef, ComponentType } from 'react';
import axios from 'axios';
import { ChevronRightIcon, ChevronDownIcon } from '@radix-ui/react-icons';

import { ErrorPage } from '../../../ErrorPage';
import { PrivatePayrollAuctionLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import * as S from './styles';
import { Warning3Icon, ShieldCheckmarkIcon, ShieldBlankIcon, InfoInformationCircleIcon, HeartIcon, Accessibility1Icon, HeartCheckmarkIcon, BriefcaseIcon, AwardMedalIcon } from '@qitech/icons';

interface ResumeOperationProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

const formatDate = (inputDate: string): string => {
    const [year, month, day] = inputDate.split('-');
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const formatPercent = (value: number): string =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

// ── Types ─────────────────────────────────────────────

type CoverageTopic = { title: string; description: string };
type CoverageEntry = { title: string; description: string; topics?: CoverageTopic[] };

interface RaffleBenefit { prefix: string; highlight: string; }

type BenefitCardKey = 'death' | 'disability' | 'critical_illness' | 'unemployment';

interface BenefitAmount { prefix?: string; highlight: string; }

interface InsuranceVariant {
    raffle: RaffleBenefit | null;
    coverages: Partial<Record<string, CoverageEntry>>;
    cardAmounts: Partial<Record<BenefitCardKey, BenefitAmount>>;
}

const INSURANCE: Record<string, InsuranceVariant> = {
    standard: {
        raffle: null,
        coverages: {
            accidental_death: { title: 'Morte por acidente', description: 'A morte do segurado, sempre que esta esteja relacionada a causas acidentais.' },
            death: { title: 'Morte', description: 'Sua família recebe até R$ 20.000 se você falecer.' },
            mqc_death: { title: 'Morte Qualquer Causa (MQC)', description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.' },
            accidental_death_fgts: {
                title: 'Morte por acidente',
                description: 'Morte do segurado, decorrente de causas acidentais ou acidente pessoal ocorrido durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Suicídio: 2 (dois) anos ininterruptos, contados a partir do início de vigência do 1º seguro. Caso exista um período intermediário sem seguro, a carência será reiniciada.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'R$ 5.000,00 ou 2x o valor do empréstimo contratado, o que for maior, limitado a R$ 13.320,00 por apólice e R$ 40.000,00 por CPF.' },
                ],
            },
            permanent_disability: { title: 'Invalidez acidental', description: 'A invalidez permanente total por acidente do segurado, decorrente de lesões físicas ocorridas por causas acidentais.' },
            ipta_disability: { title: 'Invalidez Permanente Total por Acidente (IPTA)', description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.' },
            permanent_disability_illness: { title: 'Invalidez permanente total por acidente ou doença', description: 'Receba até R$ 20.000 se um acidente grave prejudicar sua rotina.' },
            permanent_disability_fgts: {
                title: 'Invalidez por acidente',
                description: 'A invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'R$ 5.000,00 ou 2x o valor do empréstimo contratado, o que for maior, limitado a R$ 13.320,00 por apólice e R$ 40.000,00 por CPF.' },
                ],
            },
            critical_illness: { title: 'Doenças graves', description: 'Cobertura de até R$ 20.000 para ajudar no seu tratamento.' },
            unemployment: { title: 'Ajuda no desemprego', description: 'Até 6 pagamentos de R$ 1.000 se perder o emprego involuntariamente (demissão sem justa causa).' },
            involuntary_unemployment: { title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)', description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.' },
            income_loss_fgts: {
                title: 'Perda de Renda',
                description: 'Perda de salário do segurado por desemprego involuntário (CLT). Perda de renda do segurado por incapacidade física total e temporária (autônomo).',
                topics: [
                    { title: 'Carência', description: '30 dias. Caso exista um período intermediário sem seguro, a vigência será reiniciada.' },
                    { title: 'Franquia', description: '15 dias.' },
                    { title: 'Capital segurado', description: 'Valor do empréstimo contratado pelo segurado, limitado a R$ 4.000 por apólice ou no agregado do CPF, indenizável em pagamento único.' },
                ],
            },
            funeral_assistance: { title: 'Auxílio Funeral', description: 'Auxílio para o funeral do segurado, independente da causa mortis, na forma de reembolso de custos.' },
            medical_assistance: { title: 'Assistência em Medicamentos', description: 'Pronto atendimento para indicar farmácias cadastradas e até 80% de desconto em medicamentos.' },
            funeral_assistance_consignated: { title: 'Auxílio funeral', description: 'Auxílio para o funeral do segurado, independente da causa mortis, na forma de reembolso de custos.' },
            medical_assistance_consignated: { title: 'Assistência em Medicamentos', description: 'Pronto atendimento para indicar farmácias cadastradas e até 80% de desconto em medicamentos.' },
        },
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 20.000' },
            disability: { prefix: 'até', highlight: 'R$ 20.000' },
            critical_illness: { prefix: 'até', highlight: 'R$ 20.000' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },
    Zurich1: {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {},
        cardAmounts: {},
    },
    '180_seguros': {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {},
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 13.320' },
            disability: { prefix: 'até', highlight: 'R$ 13.320' },
            unemployment: { prefix: 'até', highlight: 'R$ 4.000' },
        },
    },
    Akad_Seguros1: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 1.000' },
        },
    },
    Akad_Seguros2: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '5x de R$ 1.000' },
        },
    },
    Akad_Seguros3: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },
    Ezze1: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 1.000' },
        },
    },
    Ezze2: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '5x de R$ 1.000' },
        },
    },
    Ezze3: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },
    Blindado1: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            }
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
        },
    },
};

const variantFor = (ins: string): InsuranceVariant => INSURANCE[ins] ?? INSURANCE.standard;

const resolveCoverage = (risk: string, ins: string): CoverageEntry | null =>
    variantFor(ins).coverages[risk] ?? INSURANCE.standard.coverages[risk] ?? null;

const resolveRaffle = (ins: string): RaffleBenefit | null => variantFor(ins).raffle;

const resolveBenefitAmount = (card: BenefitCardKey, ins: string): BenefitAmount | null =>
    variantFor(ins).cardAmounts[card] ?? INSURANCE.standard.cardAmounts[card] ?? null;

const BENEFIT_CARD_META: Record<BenefitCardKey, {
    description: string;
    risks: string[];
    Icon: ComponentType<{ size?: number }>;
}> = {
    death: {
        description: 'para morte natural ou acidental',
        risks: ['death', 'accidental_death', 'mqc_death', 'accidental_death_fgts'],
        Icon: HeartIcon,
    },
    disability: {
        description: 'para invalidez total por acidente',
        risks: ['permanent_disability', 'permanent_disability_illness', 'permanent_disability_fgts', 'ipta_disability'],
        Icon: Accessibility1Icon,
    },
    critical_illness: {
        description: 'para doenças graves',
        risks: ['critical_illness'],
        Icon: HeartCheckmarkIcon,
    },
    unemployment: {
        description: 'para desemprego involuntário',
        risks: ['unemployment', 'involuntary_unemployment', 'income_loss_fgts'],
        Icon: BriefcaseIcon,
    },
};

const getActiveBenefitCards = (risks: string[] | undefined, ins: string): BenefitCardKey[] => {
    if (!risks || !Array.isArray(risks)) return [];
    const risksSet = new Set(risks);
    return (Object.keys(BENEFIT_CARD_META) as BenefitCardKey[]).filter((k) =>
        BENEFIT_CARD_META[k].risks.some((r) => risksSet.has(r)) && resolveBenefitAmount(k, ins) !== null,
    );
};

const RISK_PRIORITY: Record<string, number> = {
    permanent_disability_illness: 1,
    permanent_disability_fgts: 1,
    permanent_disability: 1,
    critical_illness: 2,
    death: 3,
    unemployment: 4,
    involuntary_unemployment: 4,
    income_loss_fgts: 4,
};

const filterAndSortRisks = (risks: string[] | undefined): string[] => {
    if (!risks || !Array.isArray(risks)) return [];
    const hasMoreSpecificDisability =
        risks.includes('permanent_disability_illness') || risks.includes('permanent_disability_fgts');
    const filtered = hasMoreSpecificDisability
        ? risks.filter((r) => r !== 'permanent_disability')
        : [...risks];
    return filtered.sort((a, b) => (RISK_PRIORITY[a] ?? 1000) - (RISK_PRIORITY[b] ?? 1000));
};

const ACCOUNT_TERM_ITEMS = [
    {
        label: 'Custo Zero',
        text: 'Você não paga nenhuma taxa de abertura ou manutenção.',
    },
    {
        label: 'Uso Exclusivo',
        text: 'Serve apenas para movimentar valores do seu seguro (pagamentos e indenizações).',
    },
    {
        label: 'Organização',
        text: 'Seus recursos ficam protegidos e separados de outras contas.',
    },
    {
        label: 'Praticidade',
        text: 'Tudo acontece de forma automática e transparente, conforme as normas do BACEN e SUSEP.',
    },
];

// ── Component ────────────────────────────────────────────

export const PrivatePayrollAuctionResumeOperation = ({
    signData,
    setSignerStep,
    setSignData,
}: ResumeOperationProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);
    const text = resolveColor('text', clientThemeSettings);
    const accent = resolveColor('accent', clientThemeSettings);
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(false);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [insuranceTermsChecked, setInsuranceTermsChecked] = useState(false);
    const [accountTermsChecked, setAccountTermsChecked] = useState(false);
    const [showWithoutInsuranceConfirmation, setShowWithoutInsuranceConfirmation] = useState(false);

    // Dual credit operation state (new flow: signer chooses between with-insurance and without-insurance variants)
    const dualCreditOperations = signData?.credit_operations;
    const isDualCreditOperation = !!dualCreditOperations;
    const chosenVariant: 'with_insurance' | 'without_insurance' | undefined = dualCreditOperations?.chosen_variant;
    const isDualCreditOperationPending = isDualCreditOperation && chosenVariant == null;
    const isDualCreditOperationReadonly = isDualCreditOperation && chosenVariant != null;
    const creditOperationWithInsurance = dualCreditOperations?.with_insurance;
    const creditOperationWithoutInsurance = dualCreditOperations?.without_insurance;

    // Insurance state — in dual-pending mode, source from with_insurance variant; otherwise from legacy fields
    const insurance = isDualCreditOperationPending
        ? creditOperationWithInsurance?.insurance
        : signData?.insurance_data;
    const hasInsurance = !!insurance;
    const isInsuranceOptional = isDualCreditOperationPending || (hasInsurance && !!signData?.insurance_decision_enabled);
    const insuranceEnum: string = insurance?.insurance_data_enum || 'standard';
    const isDisbursementFlow = hasInsurance && insurance.payment_type === 'disbursement';
    const showInsuranceTerms = hasInsurance && (!isDisbursementFlow || !!insurance.term_document_url);
    const showAccountTerms = hasInsurance && !isDisbursementFlow
        && (insurance.payment_type === 'internal_account' || !!insurance.account_term_document_url);

    const [coverageOpen, setCoverageOpen] = useState(false);
    const [benefitsOpen, setBenefitsOpen] = useState(false);
    const [accountTermsOpen, setAccountTermsOpen] = useState(false);
    const [checkboxModalContext, setCheckboxModalContext] = useState<'with_insurance' | 'without_insurance'>('with_insurance');

    const { contentEndRef, hasScrolledToEnd, scrollToBottom, reset } = useScrollToBottom([]);
    const withInsuranceScrollRef = useRef<HTMLDivElement>(null);
    const checkboxModal = useAnimatedModal();
    const rejectModal = useAnimatedModal();
    const confirmModal = useAnimatedModal();

    const op = isDualCreditOperationPending ? creditOperationWithInsurance : signData?.credit_operation_data;
    const documentUrl = isDualCreditOperationPending ? creditOperationWithInsurance?.document_url : signData?.document_url;

    const raffle = hasInsurance ? resolveRaffle(insuranceEnum) : null;
    const sortedRisks = hasInsurance ? filterAndSortRisks(insurance.covered_risks) : [];
    const activeBenefitCards = hasInsurance ? getActiveBenefitCards(insurance.covered_risks, insuranceEnum) : [];

    const allCheckboxesMet = (() => {
        if (!isChecked) return false;
        if (hasInsurance) {
            if (showInsuranceTerms && !insuranceTermsChecked) return false;
            if (showAccountTerms && !accountTermsChecked) return false;
        }
        return true;
    })();

    const extractFilenameFromUrl = (url: string): string => {
        try {
            const pathname = new URL(url).pathname;
            const last = pathname.split('/').pop();
            if (!last) return 'documento.pdf';
            return decodeURIComponent(last) || 'documento.pdf';
        } catch {
            return 'documento.pdf';
        }
    };

    const handleDocumentDownload = async (url: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = extractFilenameFromUrl(url);
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(url, '_blank');
        }
    };

    const handleSubmit = async (decision?: 'accepted' | 'rejected') => {
        if (hasMadeRequest) return;

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload: any = {
            step: 'resume_operation',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        if (decision) {
            payload.decision = decision;
        }

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((error) => {
                const status = [401, 403, 409].includes((error.response || {}).status)
                    ? error.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleButtonClick = () => {
        if (!hasScrolledToEnd) {
            scrollToBottom();
            return;
        }

        if (!allCheckboxesMet) {
            setCheckboxModalContext('with_insurance');
            checkboxModal.open();
            return;
        }

        if (isInsuranceOptional) {
            confirmModal.open();
        } else {
            handleSubmit();
        }
    };

    const handleConfirmAccept = () => {
        confirmModal.close();
        handleSubmit('accepted');
    };

    const handleRejectButtonClick = () => {
        if (!hasScrolledToEnd) {
            scrollToBottom();
            return;
        }

        if (!isChecked) {
            setCheckboxModalContext('without_insurance');
            checkboxModal.open();
            return;
        }

        if (isDualCreditOperationPending) {
            reset();
            setIsChecked(false);
            setShowWithoutInsuranceConfirmation(true);
        } else {
            rejectModal.open();
        }
    };

    const handleRejectInsurance = () => {
        handleSubmit('rejected');
    };

    const renderInsuranceSection = () => {
        if (!hasInsurance) return null;
        return (
            <>
                <S.IntroCard $color={primary}>
                    <S.IntroHeader>
                        <S.IntroIconWrapper $color={primary}>
                            <ShieldBlankIcon size={24} />
                        </S.IntroIconWrapper>
                        <S.IntroTitleBlock>
                            <S.IntroTitle $color={primary}>Proteção financeira</S.IntroTitle>
                            {isInsuranceOptional && (
                                <S.IntroSubtitle>
                                    Opcional — não é obrigatória para contratar o empréstimo
                                </S.IntroSubtitle>
                            )}
                        </S.IntroTitleBlock>
                    </S.IntroHeader>
                    <S.IntroBody>
                        Um seguro adicional que cobre parcelas em caso de desemprego, doenças graves, invalidez ou morte.
                        {isInsuranceOptional && (
                            <><strong>Você pode contratar o empréstimo sem ele.</strong></>
                        )}
                    </S.IntroBody>
                </S.IntroCard>

                {(() => {
                    const disbursed = op?.disbursed_amount ?? 0;
                    const premium = insurance.premium_amount ?? 0;
                    const netWithInsurance = disbursed - premium;
                    if (isDisbursementFlow) {
                        return (
                            <S.ImpactCard>
                                <S.ImpactTitle>Valor da proteção financeira</S.ImpactTitle>
                                <S.ImpactHighlightRow $color={primary}>
                                    <S.ImpactHighlightLabelBlock>
                                        <S.ImpactHighlightLabel $color={primary}>
                                            Valor do seguro
                                        </S.ImpactHighlightLabel>
                                    </S.ImpactHighlightLabelBlock>
                                    <S.ImpactHighlightValue $color={primary}>
                                        {formatCurrency(premium)}
                                    </S.ImpactHighlightValue>
                                </S.ImpactHighlightRow>
                            </S.ImpactCard>
                        );
                    }
                    return (
                        <S.ImpactCard>
                            <S.ImpactTitle>Impacto no valor total</S.ImpactTitle>
                            <S.ImpactRow>
                                <S.ImpactLabel>Valor líquido do empréstimo</S.ImpactLabel>
                                <S.ImpactValue>{formatCurrency(disbursed)}</S.ImpactValue>
                            </S.ImpactRow>
                            <S.ImpactHighlightRow $color={primary}>
                                <S.ImpactHighlightLabelBlock>
                                    <S.ImpactHighlightLabel $color={primary}>
                                        Valor líquido com seguro
                                    </S.ImpactHighlightLabel>
                                    <S.ImpactHighlightDiff $color={primary}>
                                        -{formatCurrency(premium)} do seguro
                                    </S.ImpactHighlightDiff>
                                </S.ImpactHighlightLabelBlock>
                                <S.ImpactHighlightValue $color={primary}>
                                    {formatCurrency(netWithInsurance)}
                                </S.ImpactHighlightValue>
                            </S.ImpactHighlightRow>
                        </S.ImpactCard>
                    );
                })()}

                {/* Coberturas */}
                {sortedRisks.length > 0 && (
                    <S.CollapsibleCard>
                        <S.CollapsibleHeader
                            $borderBottom={coverageOpen}
                            onClick={() => setCoverageOpen(!coverageOpen)}
                        >
                            <S.CollapsibleTitle $color={accent}>O que está coberto</S.CollapsibleTitle>
                            <S.ChevronIcon $open={coverageOpen}>
                                <ChevronDownIcon width={20} height={20} />
                            </S.ChevronIcon>
                        </S.CollapsibleHeader>
                        <S.CollapsibleContent $open={coverageOpen}>
                            <div>
                                {sortedRisks.map((risk) => {
                                    const display = resolveCoverage(risk, insuranceEnum);
                                    if (!display) return null;
                                    return (
                                        <S.CoverageItem key={risk}>
                                            <S.CoverageTitle>{display.title}</S.CoverageTitle>
                                            <S.CoverageDescription>{display.description}</S.CoverageDescription>
                                            {display.topics && display.topics.length > 0 && (
                                                <S.CoverageTopicsList>
                                                    {display.topics.map((topic, idx) => (
                                                        <S.CoverageTopicItem key={idx}>
                                                            <S.CoverageTopicLabel>{topic.title}:</S.CoverageTopicLabel>{' '}
                                                            <S.CoverageTopicText>{topic.description}</S.CoverageTopicText>
                                                        </S.CoverageTopicItem>
                                                    ))}
                                                </S.CoverageTopicsList>
                                            )}
                                        </S.CoverageItem>
                                    );
                                })}
                            </div>
                        </S.CollapsibleContent>
                    </S.CollapsibleCard>
                )}

                {/* Beneficios */}
                {raffle && (
                    <S.CollapsibleCard>
                        <S.CollapsibleHeader
                            $borderBottom={benefitsOpen}
                            onClick={() => setBenefitsOpen(!benefitsOpen)}
                        >
                            <S.CollapsibleTitle $color={accent}>Benefícios Adicionais</S.CollapsibleTitle>
                            <S.ChevronIcon $open={benefitsOpen}>
                                <ChevronDownIcon width={20} height={20} />
                            </S.ChevronIcon>
                        </S.CollapsibleHeader>
                        <S.CollapsibleContent $open={benefitsOpen}>
                            <div>
                                <S.BenefitItem>
                                    <S.BenefitText $color={primary}>
                                        {raffle.prefix} <strong>{raffle.highlight}</strong>
                                    </S.BenefitText>
                                </S.BenefitItem>
                            </div>
                        </S.CollapsibleContent>
                    </S.CollapsibleCard>
                )}
                {/* Termos de abertura de conta */}
                {showAccountTerms && (
                    <>
                        <S.CollapsibleCard>
                            <S.CollapsibleHeader
                                $borderBottom={accountTermsOpen}
                                onClick={() => setAccountTermsOpen(!accountTermsOpen)}
                            >
                                <S.CollapsibleTitle $color={accent}>Termo de abertura de conta</S.CollapsibleTitle>
                                <S.ChevronIcon $open={accountTermsOpen}>
                                    <ChevronDownIcon width={20} height={20} />
                                </S.ChevronIcon>
                            </S.CollapsibleHeader>
                            <S.CollapsibleContent $open={accountTermsOpen}>
                                <div>
                                    <S.CollapsibleBody>
                                        <S.AccountTermIntro>
                                            Para garantir a segurança e agilidade do seu seguro,{' '}
                                            <strong>criamos uma conta vinculada ao seu crédito.</strong>
                                        </S.AccountTermIntro>
                                        {ACCOUNT_TERM_ITEMS.map((item, i) => (
                                            <S.AccountTermItem key={i}>
                                                <S.AccountTermLabel>{item.label}</S.AccountTermLabel>
                                                <S.AccountTermText>{item.text}</S.AccountTermText>
                                            </S.AccountTermItem>
                                        ))}
                                    </S.CollapsibleBody>
                                </div>
                            </S.CollapsibleContent>
                        </S.CollapsibleCard>
                    </>
                )}

                {(showInsuranceTerms || showAccountTerms) && (
                <S.InsuranceTermsCard>
                    <S.InsuranceTermsTitle>Para contratar a proteção, confirme:</S.InsuranceTermsTitle>
                    <S.InsuranceTermsList>
                        {showInsuranceTerms && (
                        <CheckboxField
                            id="insurance-terms"
                            checked={insuranceTermsChecked}
                            onChange={(e) => setInsuranceTermsChecked(e.target.checked)}
                            checkboxColor={primary}
                            textColor={text}
                        >
                            Li e concordo com os{' '}
                            {insurance.term_document_url ? (
                                <S.TermLink
                                    $color={primary}
                                    href={insurance.term_document_url}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDocumentDownload(insurance.term_document_url);
                                    }}
                                >
                                    Termos de contratação do seguro
                                </S.TermLink>
                            ) : (
                                <S.TermLink as="span" $color={primary}>Termo de contratação do seguro</S.TermLink>
                            )}
                            .
                        </CheckboxField>
                        )}
                        {showAccountTerms && (
                            <CheckboxField
                                id="account-terms"
                                checked={accountTermsChecked}
                                onChange={(e) => setAccountTermsChecked(e.target.checked)}
                                checkboxColor={primary}
                                textColor={text}
                            >
                                Li e concordo com o{' '}
                                {insurance.account_term_document_url ? (
                                    <S.TermLink
                                        href={insurance.account_term_document_url}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDocumentDownload(insurance.account_term_document_url);
                                        }}
                                        $color={primary}
                                    >
                                        Termo de abertura de conta
                                    </S.TermLink>
                                ) : (
                                    <S.TermLink as="span" $color={primary}>Termo de abertura de conta</S.TermLink>
                                )}
                                .
                            </CheckboxField>
                        )}
                    </S.InsuranceTermsList>
                </S.InsuranceTermsCard>
                )}
            </>
        );
    };

    if (loading) return <PrivatePayrollAuctionLoading />;

    if (showWithoutInsuranceConfirmation && isDualCreditOperationPending && creditOperationWithoutInsurance) {
        const wo = creditOperationWithoutInsurance;
        return (
            <S.PageWrapper>
                <S.LogoSection>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                    )}
                </S.LogoSection>
                <S.ScrollContent>
                    <S.Title $color={primary}>Resumo do contrato sem proteção</S.Title>
                    <S.DataCardContainer>
                        <S.DataRow>
                            <S.RowLabel>Valor total do empréstimo</S.RowLabel>
                            <S.RowValue $color={primary}>{formatCurrency(wo.issue_amount ?? 0)}</S.RowValue>
                        </S.DataRow>
                        <S.DataRow>
                            <S.RowLabel>IOF</S.RowLabel>
                            <S.RowValue $color={primary}>{formatCurrency(wo.iof ?? 0)}</S.RowValue>
                        </S.DataRow>
                        <S.DataRow>
                            <S.RowLabel>Valor líquido do empréstimo</S.RowLabel>
                            <S.RowValue $color={primary}>{formatCurrency(wo.disbursed_amount ?? 0)}</S.RowValue>
                        </S.DataRow>
                        <S.DataRow>
                            <S.RowLabel>Taxa de juros mensal</S.RowLabel>
                            <S.RowValue $color={primary}>{formatPercent(wo.monthly_interest_rate ?? 0)}</S.RowValue>
                        </S.DataRow>
                        <S.DataRow>
                            <S.RowLabel>Número de parcelas mensais</S.RowLabel>
                            <S.RowValue $color={primary}>{wo.number_of_installments ?? '-'}</S.RowValue>
                        </S.DataRow>
                        <S.DataRow>
                            <S.RowLabel>Data da primeira parcela</S.RowLabel>
                            <S.RowValue $color={primary}>{wo.first_due_date ? formatDate(wo.first_due_date) : '-'}</S.RowValue>
                        </S.DataRow>
                        <S.DataRow>
                            <S.RowLabel>CET (ao mês)</S.RowLabel>
                            <S.RowValue $color={primary}>{formatPercent(wo.monthly_cet ?? 0)}</S.RowValue>
                        </S.DataRow>
                    </S.DataCardContainer>
                    {wo.document_url && (
                        <S.InfoCallout $color={primary}>
                            <S.IconWrapper $color={primary}>
                                <InfoInformationCircleIcon size={24} />
                            </S.IconWrapper>
                            <S.InfoText $color={primary}>
                                Para mais informações,{' '}
                                <a
                                    href={wo.document_url}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDocumentDownload(wo.document_url);
                                    }}
                                >
                                    consulte o contrato de empréstimo sem seguro.
                                </a>
                            </S.InfoText>
                        </S.InfoCallout>
                    )}
                    <S.CheckboxWrapper>
                        <CheckboxField
                            id="resume-operation-terms-without-insurance"
                            checked={isChecked}
                            onChange={(e) => setIsChecked(e.target.checked)}
                            checkboxColor={primary}
                            textColor={text}
                        >
                            <strong style={{ color: primary }}>Confirmo que li e estou de acordo</strong> com as condições de contratação do meu empréstimo.
                        </CheckboxField>
                    </S.CheckboxWrapper>
                    <div ref={contentEndRef} />
                </S.ScrollContent>
                {error.has_error && (
                    <ErrorPage
                        error={error}
                        redirectToLogin={() => setSignerStep('login')}
                        resetError={() => setError(initialError)}
                        clientThemeSettings={clientThemeSettings}
                    />
                )}
                <S.Footer>
                    <S.SubmitButton
                        $backgroundColor={primary}
                        $disabled={!isChecked}
                        onClick={rejectModal.open}
                    >
                        Confirmar sem proteção
                        <ChevronRightIcon width={24} height={24} />
                    </S.SubmitButton>
                    <S.OutlineButton
                        $color={primary}
                        $disabled={false}
                        onClick={() => setShowWithoutInsuranceConfirmation(false)}
                    >
                        Voltar
                    </S.OutlineButton>
                    <PoweredByQITech />
                </S.Footer>
                {rejectModal.show && (
                    <S.ModalOverlay $closing={rejectModal.isClosing} onClick={rejectModal.close}>
                        <S.ModalSheet $closing={rejectModal.isClosing} onClick={(e) => e.stopPropagation()}>
                            <S.SheetHandle />
                            <S.WarningIconWrapper>
                                <S.ModalTitle $color={primary}>
                                    <Warning3Icon size={24} />
                                    Continuar sem proteção
                                </S.ModalTitle>
                            </S.WarningIconWrapper>
                            <S.ModalText>
                                Deseja continuar a contratação do seu empréstimo sem a proteção financeira?
                            </S.ModalText>
                            <S.ModalButtonRow>
                                <S.SubmitButton
                                    $backgroundColor={primary}
                                    $disabled={false}
                                    onClick={() => { rejectModal.close(); handleRejectInsurance(); }}
                                >
                                    Continuar sem proteção
                                    <ChevronRightIcon width={24} height={24} />
                                </S.SubmitButton>
                                <S.TextButton $color={primary} onClick={() => { rejectModal.close(); setShowWithoutInsuranceConfirmation(false); setTimeout(() => { withInsuranceScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' }); window.scrollTo({ top: 0, behavior: 'auto' }); }, 0); }}>
                                    Revisar as vantagens da proteção
                                    <ChevronRightIcon width={18} height={18} />
                                </S.TextButton>
                            </S.ModalButtonRow>
                        </S.ModalSheet>
                    </S.ModalOverlay>
                )}
            </S.PageWrapper>
        );
    }

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ScrollContent ref={withInsuranceScrollRef}>
                {isDualCreditOperationPending && renderInsuranceSection()}
                {/* ── Credit operation resume ── */}
                <S.Title $color={primary}>
                    {isDualCreditOperationPending ? 'Resumo do contrato com proteção' : 'Resumo do contrato'}
                </S.Title>

                <S.DataCardContainer>
                    <S.DataRow>
                        <S.RowLabel>Valor total do empréstimo</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatCurrency(op?.issue_amount ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>IOF</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatCurrency(op?.iof ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Valor líquido do empréstimo</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatCurrency(op?.disbursed_amount ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Taxa de juros mensal</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatPercent(op?.monthly_interest_rate ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Número de parcelas mensais</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {op?.number_of_installments ?? '-'}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Data da primeira parcela</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {op?.first_due_date ? formatDate(op.first_due_date) : '-'}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>CET (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatPercent(op?.monthly_cet ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                </S.DataCardContainer>

                {documentUrl && (
                    <S.InfoCallout $color={primary}>
                        <S.IconWrapper $color={primary}>
                            <InfoInformationCircleIcon size={24} />
                        </S.IconWrapper>
                        <S.InfoText $color={primary}>
                            Para mais informações,{' '}
                            <a
                                href={documentUrl}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleDocumentDownload(documentUrl);
                                }}
                            >
                                {isDualCreditOperationPending
                                    ? 'consulte o contrato de empréstimo com seguro.'
                                    : 'consulte o contrato de empréstimo.'}
                            </a>
                        </S.InfoText>
                    </S.InfoCallout>
                )}

                <S.CheckboxWrapper>
                    <CheckboxField
                        id="resume-operation-terms"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                        checkboxColor={primary}
                        textColor={text}
                    >
                        <strong style={{color: primary}}>Confirmo que li e estou de acordo</strong> com as condições de contratação do meu empréstimo.
                    </CheckboxField>
                </S.CheckboxWrapper>

                {!isDualCreditOperationPending && renderInsuranceSection()}

                <div ref={contentEndRef} />
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.Footer>
                {!isDualCreditOperationReadonly && (
                    isInsuranceOptional ? (
                        <>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={!allCheckboxesMet}
                                onClick={handleButtonClick}
                            >
                                <ShieldCheckmarkIcon size={20} />
                                Contratar com proteção
                            </S.SubmitButton>
                            <S.OutlineButton
                                $color={primary}
                                $disabled={!isChecked}
                                onClick={handleRejectButtonClick}
                            >
                                Contratar sem proteção
                            </S.OutlineButton>
                        </>
                    ) : (
                        <S.SubmitButton
                            $backgroundColor={primary}
                            $disabled={!allCheckboxesMet}
                            onClick={handleButtonClick}
                        >
                            {hasInsurance ? 'Continuar' : 'Aceitar termos do contrato'}
                            <ChevronRightIcon width={24} height={24} />
                        </S.SubmitButton>
                    )
                )}

                <PoweredByQITech />
            </S.Footer>

            {/* Checkbox reminder modal */}
            {checkboxModal.show && (
                <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                    <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.SheetHandle />
                        <S.ModalTitle $color={primary}>Confirmação pendente</S.ModalTitle>
                        <S.ModalText style={{ textAlign: 'start' }}>
                            Para continuar, você precisa marcar{' '}
                            {checkboxModalContext === 'without_insurance' ? (
                                <>
                                    a seguinte caixa acima:
                                    <ul>
                                        <li><strong>Aceite das condições do empréstimo</strong></li>
                                    </ul>
                                </>
                            ) : (
                                <>
                                    as seguintes caixas acima:
                                    <ul>
                                        <li><strong>Aceite das condições do empréstimo</strong></li>
                                        {showInsuranceTerms && (
                                            <li><strong>Termos de contratação do seguro</strong></li>
                                        )}
                                        {showAccountTerms && (
                                            <li><strong>Termo de abertura de conta</strong></li>
                                        )}
                                    </ul>
                                </>
                            )}
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={false}
                                onClick={checkboxModal.close}
                            >
                                Entendi
                                <ChevronRightIcon width={24} height={24} />
                            </S.SubmitButton>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}

            {/* Reject insurance confirmation modal */}
            {rejectModal.show && (
                <S.ModalOverlay $closing={rejectModal.isClosing} onClick={rejectModal.close}>
                    <S.ModalSheet $closing={rejectModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.SheetHandle />
                        <S.WarningIconWrapper>
                            <S.ModalTitle $color={primary}>
                                <Warning3Icon size={24} />
                                Continuar sem proteção
                            </S.ModalTitle>
                        </S.WarningIconWrapper>
                        <S.ModalText>
                            Deseja continuar a contratação do seu empréstimo sem a proteção financeira?
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={false}
                                onClick={() => { rejectModal.close(); handleRejectInsurance(); }}
                            >
                                Continuar sem proteção
                                <ChevronRightIcon width={24} height={24} />
                            </S.SubmitButton>
                            <S.TextButton $color={primary} onClick={() => { rejectModal.close(); setShowWithoutInsuranceConfirmation(false); setTimeout(() => { withInsuranceScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' }); window.scrollTo({ top: 0, behavior: 'auto' }); }, 0); }}>
                                Revisar as vantagens da proteção
                                <ChevronRightIcon width={18} height={18} />
                            </S.TextButton>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}

            {/* Accept insurance confirmation modal */}
            {confirmModal.show && hasInsurance && (
                <S.ModalOverlay $closing={confirmModal.isClosing} onClick={confirmModal.close}>
                    <S.ModalSheet $closing={confirmModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.SheetHandle />
                        <S.WarningIconWrapper>
                            <S.ModalTitle $color={primary}>
                                <ShieldCheckmarkIcon size={24} />
                                Confirmar contratação
                            </S.ModalTitle>
                        </S.WarningIconWrapper>
                        <S.ConfirmModalText>
                            Você está contratando o empréstimo{' '}
                            <strong>com a proteção financeira opcional</strong>. Confira as seguintes informações:
                        </S.ConfirmModalText>

                        {activeBenefitCards.length > 0 && (
                            <S.BenefitsSection>
                                {raffle && (
                                    <S.RaffleBanner $color={primary}>
                                        <S.RaffleIconWrapper>
                                            <AwardMedalIcon size={28} />
                                        </S.RaffleIconWrapper>
                                        <S.RaffleTextBlock>
                                            <S.RaffleTitle>
                                                {raffle.prefix} <strong>{raffle.highlight}</strong>
                                            </S.RaffleTitle>
                                            <S.RaffleSubtitle>
                                                Contrate agora e participe do próximo sorteio!
                                            </S.RaffleSubtitle>
                                        </S.RaffleTextBlock>
                                    </S.RaffleBanner>
                                )}

                                <S.BenefitCardsGrid>
                                    {activeBenefitCards.map((key) => {
                                        const { description, Icon } = BENEFIT_CARD_META[key];
                                        const amount = resolveBenefitAmount(key, insuranceEnum);
                                        if (!amount) return null;
                                        return (
                                            <S.BenefitCard key={key} $color={primary}>
                                                <S.BenefitCardIcon $color={primary}>
                                                    <Icon size={28} />
                                                </S.BenefitCardIcon>
                                                <S.BenefitCardAmount $color={primary}>
                                                    {amount.prefix && <>{amount.prefix} </>}
                                                    <strong>{amount.highlight}</strong>
                                                </S.BenefitCardAmount>
                                                <S.BenefitCardDescription>{description}</S.BenefitCardDescription>
                                            </S.BenefitCard>
                                        );
                                    })}
                                </S.BenefitCardsGrid>
                            </S.BenefitsSection>
                        )}

                        <S.ConfirmModalTable $color={primary}>
                            <S.ConfirmModalRow $color={primary}>
                                <S.ConfirmModalRowLabel>Valor do Seguro (opcional)</S.ConfirmModalRowLabel>
                                <S.ConfirmModalRowValue $color={primary}>
                                    {formatCurrency(insurance.premium_amount ?? 0)}
                                </S.ConfirmModalRowValue>
                            </S.ConfirmModalRow>
                            {!isDisbursementFlow && (
                                <S.ConfirmModalRow $divider $color={primary}>
                                    <S.ConfirmModalRowLabel $bold>Novo valor líquido</S.ConfirmModalRowLabel>
                                    <S.ConfirmModalRowValue $color={primary} $bold>
                                        {formatCurrency((op?.disbursed_amount ?? 0) - (insurance.premium_amount ?? 0))}
                                    </S.ConfirmModalRowValue>
                                </S.ConfirmModalRow>
                            )}
                        </S.ConfirmModalTable>
                        <S.ConfirmModalHint>
                            A proteção não é obrigatória. Você pode voltar e continuar sem ela a qualquer momento antes de assinar. A proteção é válida durante o prazo do empréstimo, com cobertura válida até o término do contrato.
                        </S.ConfirmModalHint>
                        <S.ModalButtonRow>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={false}
                                onClick={handleConfirmAccept}
                            >
                                Contratar com proteção
                                <ChevronRightIcon width={24} height={24} />
                            </S.SubmitButton>
                            <S.TextButton $color={primary} onClick={confirmModal.close}>
                                Voltar e revisar
                                <ChevronRightIcon width={18} height={18} />
                            </S.TextButton>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </S.PageWrapper>
    );
};
